import {useNavigate, useParams} from "react-router-dom";
import {Box, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import * as React from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ContentCategoryService} from "crqlar-react-common/dist/services/contentCategory/contentCategory.service";
import {ContentCategory, ContentStatus} from "crqlar-react-common/dist/models/content/contentCategory.model";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import ImageUploadField, {
    ALL_IMAGE_UPLOAD_RATIOS
} from "crqlar-react-common/dist/components/ImageUploadField";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

const ContentCategoryDetails = () => {

    //region State

    useTrackScreenVisit('system content category details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const readOnly = useReadOnly()

    const contentCategoryData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return ContentCategoryService.getContentCategoryById(id)
            } else {
                return {}
            }
        },
        create: ContentCategoryService.createContentCategory,
        update: ContentCategoryService.updateContentCategory,
        delete: (id) => ContentCategoryService.deleteContentCategory({id: [id]}),
        onDeleted: () => navigate('/system/contentCategories', {replace: true}),
        onSaved: (contentCategory, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/contentCategories/${contentCategory.id}`, {replace: true})
            }
        },
        isValid: (contentCategory) => {
            return !!contentCategory.name
        },
        cleanupBeforeSave: (contentCategory) => {
            return {
                ...contentCategory,
                name: contentCategory.name?.trim()
            }
        }
    }, id)

    const contentCategory = contentCategoryData.item
    const setContentCategory = contentCategoryData.setItem
    const nameMissing = isRequiredTextMissing(contentCategoryData, contentCategory.name)

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof ContentCategory, value: any) => {
        setContentCategory({...contentCategory, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={contentCategory.name}
            data={contentCategoryData}
            back={() => navigate('/system/contentCategories')}
        />
    )

    const imageSection = (
        <ImageUploadField
            id={'image-picker'}
            image={contentCategory.media}
            setImage={(image) => updateField('media', image)}
            ratio={'landscape'}
            ratioOptions={ALL_IMAGE_UPLOAD_RATIOS}
            disabled={readOnly}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this content category in content setup and guest-facing surfaces.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={contentCategory.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Tag')}
                    tooltip={t('Short grouping key used to identify this category in content and integrations.')}
                >
                    <TextField
                        id={'tag'}
                        size={'small'}
                        value={contentCategory.tag}
                        onChange={(e) => updateField('tag', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('Status')}
                    tooltip={t('Controls whether content in this category is active or still draft.')}
                >
                    <NewStatusPicker<ContentStatus>
                        value={contentCategory.status || ContentStatus.DRAFT}
                        onChange={(value) => updateField('status', value)}
                        ariaLabel={t('Status')}
                        options={[
                            {value: ContentStatus.DRAFT, label: t('Draft')},
                            {value: ContentStatus.ACTIVE, label: t('Active')},
                        ]}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const translationsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Translations')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name translations')}
                    tooltip={t('Localized category names shown in translated interfaces.')}
                >
                    <LocalizedTextField
                        id={'nameLocalized'}
                        size={'small'}
                        value={contentCategory.nameLocalized}
                        onChange={(e) => updateField('nameLocalized', e)}
                        fullWidth
                        required
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete content category')}
                description={t('This permanently deletes the content category. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={contentCategoryData.saving}
                disabled={readOnly}
                onDelete={() => contentCategoryData.removeNow()}
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {contentCategory.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {imageSection}
                {basicInfoSection}
                <Divider/>
                {translationsSection}

                {
                    !!contentCategory.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    );
}

export default ContentCategoryDetails;
